// src/encounter/lib/to-submitted-questionnaire.ts
// Map the wire QuestionnaireSubmitted → a flat view model for the rail's Special-Form section
// (PROD-1251). Mirrors Flutter pre_encounter_widget.dart: the section renders only when
// questionnaire.questionnaireid != null (body() gate); group items are flattened (the
// FetchFormResponseState listener spreads type==group children); each answer is stringified by type
// via questionResponseString() (:119-144). Pure, but for the `formNameCase` display switch.
import type { SubmittedQuestionnaire, SubmittedItem } from '@/types/questionnaire-response-api';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';

export interface SubmittedFormRow {
  id: string;
  question: string;
  /** Stringified answer(s) — Choice/OpenChoice can have several codings. */
  answers: string[];
}

export interface SubmittedFormView {
  name: string;
  updatedByName?: string;
  rows: SubmittedFormRow[];
}

const EMPTY = '—';

/** Flatten one level of group nesting (Flutter only flattens top-level groups). */
function flatten(items: SubmittedItem[] | null | undefined): SubmittedItem[] {
  const out: SubmittedItem[] = [];
  for (const it of items ?? []) {
    if ((it.type ?? '').toLowerCase() === 'group') {
      out.push(...(it.items ?? []));
    } else {
      out.push(it);
    }
  }
  return out;
}

/** Stringify an item's answer by type — String / Boolean / Choice / OpenChoice (else em-dash). */
function answersFor(item: SubmittedItem): string[] {
  const type = (item.type ?? '').toLowerCase();
  const a = item.answer;
  if (type === 'string') return [a?.valueString?.trim() || EMPTY];
  if (type === 'boolean') {
    // Only render Yes/No for an ACTUAL boolean — a missing value must not become a fabricated
    // clinical "No" (Codex). Flutter does `?? false`→"No"; this is a deliberate, safer divergence.
    if (typeof a?.valueBoolean !== 'boolean') return [EMPTY];
    return [a.valueBoolean ? 'Yes' : 'No'];
  }
  if (type === 'choice' || type === 'openchoice') {
    const vals = (a?.valueCoding ?? []).map((c) => c.display?.trim()).filter((d): d is string => !!d);
    return vals.length ? vals : [EMPTY];
  }
  return [EMPTY];
}

/** The questionnaire's name as stored, or with its first letter in capitals under `?x=form-name-case` (dev default, Samer 3 Nov:
 *  dev's "first time" reads "First time"). Only the first letter changes, so names like "PHQ-9" stay as they are. */
function formName(raw: string | null | undefined): string {
  const name = raw?.trim() || 'Submitted form';
  return isEncounterFlagOn('formNameCase') ? name.charAt(0).toUpperCase() + name.slice(1) : name;
}

/**
 * Build the Special-Form view model, or null when there is no submitted questionnaire
 * (questionnaire.questionnaireid is null/absent — the Flutter render gate). When present but with no
 * items, returns an empty `rows` (header + verified check still render).
 */
export function toSubmittedForm(data: SubmittedQuestionnaire | null | undefined): SubmittedFormView | null {
  const qid = data?.questionnaire?.questionnaireid;
  if (!qid) return null;
  const rows: SubmittedFormRow[] = flatten(data?.item).map((it, i) => ({
    id: `qr-${i}`,
    question: (it.text ?? '').trim() || EMPTY,
    answers: answersFor(it),
  }));
  return {
    name: formName(data?.questionnaireName),
    updatedByName: data?.updatedByName?.trim() || undefined,
    rows,
  };
}
